"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { getAuthFlashError, startAuthIntent, toAbsoluteApiUrl } from "@/core/auth/auth-api";
import { type AuthEntryPoint, type AuthFlashError } from "@/core/auth/types";
import { useAsyncAction } from "@/shared/hooks/use-async-action";
import { resolveOrNull } from "@/shared/lib/async-result";
import Env from "@/core/config/env";

interface AuthEntryHeroActionsProps {
  entryPoint: AuthEntryPoint;
  loadingLabel: string;
  primaryLabel: string;
  returnTo?: string;
  secondaryHref: string;
  secondaryLabel: string;
}

export function AuthEntryHeroActions({
  entryPoint,
  loadingLabel,
  primaryLabel,
  returnTo,
  secondaryHref,
  secondaryLabel,
}: AuthEntryHeroActionsProps) {
  const continueAction = useAsyncAction<void>();
  const [flashError, setFlashError] = useState<AuthFlashError | null>(null);
  const visibleError = continueAction.state.error?.message ?? flashError?.message;

  useEffect(() => {
    let isActive = true;

    void resolveOrNull(async () => {
      const nextFlashError = await getAuthFlashError();

      if (isActive) {
        setFlashError(nextFlashError);
      }

      return nextFlashError;
    });

    return () => {
      isActive = false;
    };
  }, []);

  async function handleContinue() {
    setFlashError(null);

    await continueAction.run(async () => {
      const response = await startAuthIntent(entryPoint, returnTo);
      const loginTarget = response.loginUrl
        ? toAbsoluteApiUrl(response.loginUrl)
        : Env.apiEndpoint("/auth/login");

      window.location.assign(loginTarget);
    });
  }

  return (
    <div className="w-full max-w-xl space-y-4">
      {visibleError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {visibleError}
        </div>
      ) : null}
      <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button
          className="h-12 min-w-[220px] rounded-full text-base"
          disabled={continueAction.state.loading}
          onClick={handleContinue}
        >
          {continueAction.state.loading ? loadingLabel : primaryLabel}
          <ArrowRight className="size-4" />
        </Button>
        <Button
          className="h-12 min-w-[220px] rounded-full border-white/22 bg-white/8 text-base text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white hover:text-slate-950"
          render={<Link href={secondaryHref} />}
          variant="outline"
        >
          {secondaryLabel}
        </Button>
      </div>
    </div>
  );
}
